import type { Command, ParseError } from './commands';

/**
 * Deterministic grammar for the scene editor (Phase 3A). Commands that say "selected"
 * act on the explicit UI selection (objects, layer, character) and fail clearly when
 * nothing is selected. Shape requests that mention a motion ("… that spins") are left to
 * the Phase 0 animated-shape grammar.
 */
type Result = { command: Command } | { error: ParseError } | null;

const err = (segment: string, message: string, example: string): Result => ({ error: { kind: 'invalid-value', message, segment, suggestions: [example] } });

const OBJ = String.raw`(?:the |this |that )?(?:selected |current )?(?:object|objects|shape|shapes|item|items|selection|it)`;
const CHAR_REF = String.raw`(?:the |this |that )?(?:selected |current )?character`;
const LAYER_REF = String.raw`(?:the |this |that )?(?:selected |current )?layer`;
const MOTION_WORDS = /\b(orbits?|orbiting|circling|pulses?|pulsing|bounces?|bouncing|spins?|spinning|rotates?|rotating)\b/;

export const SCENE_PRESET_WORDS: [RegExp, 'neon-city' | 'dark-studio' | 'forest'][] = [
  [/^neon( city)?$|^cyberpunk( city)?$|^night city$/, 'neon-city'],
  [/^dark studio$|^studio$|^stage$/, 'dark-studio'],
  [/^forest( landscape)?$|^landscape$|^woods?$/, 'forest'],
];

export const CHARACTER_PRESET_WORDS: [RegExp, 'modern' | 'fantasy' | 'futuristic'][] = [
  [/^(modern|runner|modern runner|casual)$/, 'modern'],
  [/^(fantasy|adventurer|fantasy adventurer|medieval|knight)$/, 'fantasy'],
  [/^(futuristic|sci-?fi|sci fi|scifi|space|sci-?fi explorer|explorer|robot)$/, 'futuristic'],
];

const SHAPE_WORDS: Record<string, 'rect' | 'circle' | 'ellipse' | 'triangle'> = {
  rectangle: 'rect',
  rect: 'rect',
  square: 'rect',
  box: 'rect',
  circle: 'circle',
  dot: 'circle',
  ball: 'circle',
  ellipse: 'ellipse',
  oval: 'ellipse',
  triangle: 'triangle',
};

/** Parses a scene-editor segment. `parseColor` is the shared colour parser (names and hex). */
export function parseSceneSegment(segment: string, parseColor: (text: string) => string | undefined): Result {
  let m: RegExpMatchArray | null;

  // ---- Scenes
  if ((m = segment.match(/^(?:create|make|new|add|start|build) (?:a |an |the )?(?:new )?(.+?) scene$/))) {
    const what = m[1]!.trim();
    if (/^(blank|empty)$/.test(what)) return { command: { type: 'scene.create', preset: 'blank' } };
    const preset = SCENE_PRESET_WORDS.find(([re]) => re.test(what))?.[1];
    if (preset) return { command: { type: 'scene.create', preset } };
    return err(segment, `I can create a neon city, dark studio, forest landscape or blank scene — not “${what}”.`, 'create a neon city scene');
  }

  // ---- Characters
  if ((m = segment.match(/^(?:create|make|new|add|design) (?:a |an )?(?:new )?(.+?) (?:character|hero|person)$/))) {
    const preset = CHARACTER_PRESET_WORDS.find(([re]) => re.test(m![1]!.trim()))?.[1];
    if (preset) return { command: { type: 'character.create', preset } };
    return err(segment, `Character presets are modern, fantasy and futuristic — not “${m[1]}”.`, 'create a futuristic character');
  }
  if (new RegExp(`^(?:place|put|add|drop) ${CHAR_REF}(?: (?:in|into|on|to) (?:the )?(?:scene|stage|canvas))?$`).test(segment)) {
    return { command: { type: 'character.place' } };
  }

  // ---- Layers
  if ((m = segment.match(new RegExp(`^(hide|show|unhide|lock|unlock) ${LAYER_REF}$`)))) {
    const verb = m[1]!;
    if (verb === 'lock' || verb === 'unlock') return { command: { type: 'layer.lock', locked: verb === 'lock' } };
    return { command: { type: 'layer.visibility', visible: verb !== 'hide' } };
  }

  // ---- Adding objects (no motion words: those are Phase 0 animated shapes)
  if ((m = segment.match(/^(?:add|draw|create|insert|place) (?:a |an |one |some )?(?:new )?(.+)$/)) && !MOTION_WORDS.test(segment)) {
    const words = m[1]!.trim();
    const text = words.match(/^(?:text|label|title|caption)(?: (?:saying|that says|with))? ["“']?(.+?)["”']?$/);
    if (text) return { command: { type: 'scene.addObject', shape: 'text', text: text[1]!.slice(0, 200) } };
    const parts = words.split(' ');
    const shapeWord = parts[parts.length - 1]!;
    const shape = SHAPE_WORDS[shapeWord];
    if (shape) {
      const colorText = parts.slice(0, -1).join(' ');
      if (!colorText) return { command: { type: 'scene.addObject', shape } };
      const color = parseColor(colorText);
      if (!color) return err(segment, `Unknown colour “${colorText}”. Use a name like red or dark purple, or a hex code.`, 'add a red circle');
      return { command: { type: 'scene.addObject', shape, color } };
    }
  }

  // ---- Selected objects
  if ((m = segment.match(new RegExp(`^(?:move|shift|nudge) ${OBJ} (?:by )?(-?\\d+(?:\\.\\d+)?) ?(?:pixels?|px)? (?:to the )?(left|right|up|down)$`)))) {
    const amount = Number(m[1]);
    if (!Number.isFinite(amount) || Math.abs(amount) > 10000) return err(segment, 'Move by at most 10 000 pixels.', 'move the selected object 50 pixels right');
    const dir = m[2]!;
    const dx = dir === 'left' ? -amount : dir === 'right' ? amount : 0;
    const dy = dir === 'up' ? -amount : dir === 'down' ? amount : 0;
    return { command: { type: 'object.move', dx, dy } };
  }
  if ((m = segment.match(new RegExp(`^(?:make|scale) ${OBJ} (larger|bigger|smaller|tinier|twice as (?:big|large)|half (?:the )?size|half as (?:big|large))$`)))) {
    const w = m[1]!;
    const factor = /twice/.test(w) ? 2 : /half/.test(w) ? 0.5 : /larger|bigger/.test(w) ? 1.25 : 0.8;
    return { command: { type: 'object.scale', factor } };
  }
  if ((m = segment.match(new RegExp(`^scale ${OBJ} (?:by |to )?(\\d+(?:\\.\\d+)?) ?(%|x|×)$`)))) {
    const v = Number(m[1]);
    const factor = m[2] === '%' ? v / 100 : v;
    if (!(factor >= 0.05 && factor <= 20)) return err(segment, 'Scale between 5% and 2000%.', 'scale the selected object by 150%');
    return { command: { type: 'object.scale', factor } };
  }
  if ((m = segment.match(new RegExp(`^(?:move|bring|send|put) ${OBJ} (?:to )?(?:the )?(front|back|forward|backward|backwards|to front|to back)$`)))) {
    const w = m[1]!.replace(/^to /, '');
    return { command: { type: 'object.order', where: w === 'backwards' ? 'backward' : (w as 'front' | 'back' | 'forward' | 'backward') } };
  }
  if ((m = segment.match(new RegExp(`^rotate ${OBJ} (?:by )?(-?\\d+(?:\\.\\d+)?) ?(?:degrees?|°)?(?: (clockwise|counter-?clockwise|anticlockwise|left|right))?$`)))) {
    const deg = Number(m[1]);
    if (!Number.isFinite(deg) || Math.abs(deg) > 360) return err(segment, 'Rotate by at most 360 degrees.', 'rotate the selected object 45 degrees');
    const ccw = /counter|anti|left/.test(m[2] ?? '');
    return { command: { type: 'object.rotate', degrees: ccw ? -deg : deg } };
  }
  if ((m = segment.match(new RegExp(`^(?:make|colou?r|paint|fill|turn) ${OBJ} (?:colou?r )?(.+)$`)))) {
    const color = parseColor(m[1]!.trim());
    if (color) return { command: { type: 'object.color', color } };
    if (!/^(larger|bigger|smaller|tinier|visible|invisible|hidden)$/.test(m[1]!.trim())) return err(segment, `Unknown colour “${m[1]}”.`, 'make the selected object red');
  }
  if ((m = segment.match(new RegExp(`^(hide|show|unhide|lock|unlock) ${OBJ}$`)))) {
    const verb = m[1]!;
    if (verb === 'lock' || verb === 'unlock') return { command: { type: 'object.lock', locked: verb === 'lock' } };
    return { command: { type: 'object.visibility', visible: verb !== 'hide' } };
  }
  if (new RegExp(`^(?:duplicate|copy|clone) ${OBJ}$`).test(segment)) return { command: { type: 'object.duplicate' } };
  if (new RegExp(`^(?:delete|remove) ${OBJ}$`).test(segment)) return { command: { type: 'object.delete' } };

  // Requests for generated imagery are explained, not guessed.
  if (/\b(generate|render|paint|draw) (?:a |an )?(?:realistic |photo|ai|3d)/.test(segment) || /\b(3d|three-?d)\b/.test(segment)) {
    return { error: { kind: 'unsupported', message: 'Image generation and 3D are not available. Scenes are built from shapes, text, imported images and procedural characters.', segment, suggestions: ['create a neon city scene', 'create a futuristic character'] } };
  }
  return null;
}

export const isSceneCommand = (c: Command) => /^(scene|object|character|layer)\./.test(c.type);
